import Container, { Service } from 'typedi'
import { AxiosResponse } from 'axios'
import IHttpClientGetParameters from '@/modules/common/contracts/http-client/http-client-get-parameters.interface'
import HttpClient from '@/modules/common/services/http-client.service'
import IHttpClientPostParameters from '@/modules/common/contracts/http-client/http-client-post-parameters.interface'
import IHttpClientPatchParameters from '@/modules/common/contracts/http-client/http-client-patch-parameters.interface'

@Service()
export default class AttendeeService {
  public async getAttendees (event_uuid: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${event_uuid}/participants`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async getAttendeesListView (
    event_uuid: string,
    options: {
      limit?: number;
      skip?: number;
      listViewFields?: any[];
      status?: string;
      referral_code?: string;
      statusName?: string;
      customField?: { field: string; value: string };
      promo_code?: string;
      promo_code_uuid?: string;
      meta?: number;
      requiredFields?: string[];
      optionSelection?: { option_group_uuid: string; option_uuid: string; status?: boolean };
      badge_print_status?: string;
    } = {}
  ): Promise<AxiosResponse<any>> {
    const {
      limit = 1000,
      skip = 0,
      listViewFields,
      status,
      referral_code,
      statusName,
      customField,
      promo_code,
      promo_code_uuid,
      meta,
      requiredFields,
      optionSelection,
      badge_print_status
    } = options

    let fieldStr = ''
    if (listViewFields) {
      for (const item in listViewFields) {
        fieldStr += `&fields[]=${listViewFields[item].field}`
      }
    }

    let filterStr = ''
    if (status) {
      filterStr += `&filter[status]=${status}`
    }
    if (referral_code) {
      filterStr += `&filter[referral_code]=${referral_code}`
      if (status && statusName) {
        filterStr += `&filter[${statusName}]=${status}`
      }
    }
    if (customField) {
      filterStr += `&filter[${customField.field}]=${customField.value}`
    }
    if (promo_code) {
      filterStr += `&filter[promo_code]=${promo_code}`
    }
    if (promo_code_uuid) {
      filterStr += `&filter[promo_code_uuid]=${promo_code_uuid}`
    }
    if (optionSelection) {
      filterStr += `&filter[${optionSelection.option_group_uuid}]=${optionSelection.option_uuid}`
      if (optionSelection.status) {
        filterStr += `&filter[status]=${optionSelection.status}`
      }
    }
    if (badge_print_status) {
      filterStr += `&filter[badge_print_status]=${badge_print_status}`
    }
    if (meta) {
      fieldStr += '&meta=true'
    }

    const requiredFieldStr = ''
    if (requiredFields) {
      for (const item in requiredFields) {
        fieldStr += `&required_fields[]=${requiredFields[item]}`
      }
    }

    const httpParams: IHttpClientGetParameters = {
      url: `event/${event_uuid}/participants-list-view?limit=${limit}&skip=${skip}${fieldStr}${filterStr}${requiredFieldStr}`,
      requireToken: true
    }

    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async exportParticipantScans (event_uuid: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${event_uuid}/sessions/attendance-counts/participants/export`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async addAttendee (event_uuid: any, data: any): Promise<any> {
    const httpParams: IHttpClientPostParameters = {
      url: `event/${event_uuid}/participant`,
      requireToken: true,
      payload: data
    }
    const response = await Container.get(HttpClient).post(httpParams)
    return response
  }

  public async deleteAttendees (payload: any): Promise<any> {
    const httpParams: IHttpClientPostParameters = {
      url: `event/${payload.event_uuid}/participants`,
      requireToken: true,
      config: {
        headers: {
          environment: 'dev'
        },
        data: {
          records: payload.records
        }
      }
    }
    const response = await Container.get(HttpClient).delete(httpParams)
    return response
  }

  public async getAttendee (payload: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participant/${payload.attendee_uuid}${payload.queryParam}`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async getAttendeeStatistics (payload: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participant/${payload.attendee_uuid}/statistics`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async getAttendeeFees (payload: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participant/${payload.attendee_uuid}/fees${payload.queryParam}`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async getDeletedAttendees (event_uuid: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${event_uuid}/participants/deleted`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async restoreAttendee (payload: { event_uuid: string; attendee_uuid: string }): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientPatchParameters = {
      url: `event/${payload.event_uuid}/participants/${payload.attendee_uuid}/restore`,
      requireToken: true,
      payload: {}
    }
    const response = await Container.get(HttpClient).patch<any>(httpParams)
    return response
  }

  public async bulkRestoreAttendees (payload: { event_uuid: string; uuids: string[] }): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientPostParameters = {
      url: `event/${payload.event_uuid}/participants/bulk-restore`,
      requireToken: true,
      payload: { uuids: payload.uuids }
    }
    const response = await Container.get(HttpClient).post<any>(httpParams)
    return response
  }

  public async getAttendeeActivityLog (payload: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participant/${payload.attendee_uuid}/audit-logs${payload.queryParam}`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async searchAttendees (payload: any): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participants/search`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get<any>(httpParams)
    return response
  }

  public async getAttendeesSimple (payload: { event_uuid: string; filter_field?: string; filter_value?: string }): Promise<AxiosResponse<any>> {
    payload.filter_value = payload.filter_value?.replace('+', '%2B').replace('&', '%26')
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participants-simple` + (payload.filter_field ? `?filter[${payload.filter_field}]=${payload.filter_value}` : ''),
      requireToken: true
    }
    return await Container.get(HttpClient).get<any>(httpParams)
  }

  public async searchParticipantsSimple (payload: { event_uuid: string; keyword: string; limit?: number }): Promise<AxiosResponse<any>> {
    const query = `filter[keyword]=${encodeURIComponent(payload.keyword)}&limit=${payload.limit ?? 26}`
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participants-simple?${query}`,
      requireToken: true
    }
    return await Container.get(HttpClient).get<any>(httpParams)
  }

  public async postParticipantSimple (payload: { event_uuid: string; participant_uuid: string; data: any }): Promise<AxiosResponse<any>> {
    const httpParams: IHttpClientPostParameters = {
      url: `event/${payload.event_uuid}/participants-simple`,
      requireToken: true,
      payload: { id: payload.participant_uuid, ...payload.data }
    }
    return await Container.get(HttpClient).post<any>(httpParams)
  }

  public async searchKioskParticipants (payload: {
    event_uuid: string
    first_name?: string
    last_name?: string
    email?: string
    barcodes?: string
    match_type?: 'partial'
    filter_status?: string
    filter_payment_status?: string
  }): Promise<AxiosResponse<any>> {
    let queryString = ''
    if (payload.first_name) queryString += `&filter[first_name]=${encodeURIComponent(payload.first_name)}`
    if (payload.last_name) queryString += `&filter[last_name]=${encodeURIComponent(payload.last_name)}`
    if (payload.email) queryString += `&filter[email]=${encodeURIComponent(payload.email)}`
    if (payload.barcodes) queryString += `&filter[barcodes]=${encodeURIComponent(payload.barcodes)}`
    if (payload.match_type) queryString += `&match_type=${payload.match_type}`
    if (payload.filter_status) queryString += `&filter[status]=${encodeURIComponent(payload.filter_status)}`
    if (payload.filter_payment_status) queryString += `&filter[payment_status]=${encodeURIComponent(payload.filter_payment_status)}`
    const httpParams: IHttpClientGetParameters = {
      url: `event/${payload.event_uuid}/participants-simple?${queryString.slice(1)}`,
      requireToken: true
    }
    return await Container.get(HttpClient).get<any>(httpParams)
  }
}
